import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AdirDetailsSideNavigationComponent } from '@projects/adir/src/app/pages/adir-details/adir-details-side-navigation/adir-details-side-navigation.component';
import { ActionTile } from '@shared/src/lib/components/action-tile/action-tile.interface';
import { BreadcrumbComponent } from '@shared/src/lib/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '@shared/src/lib/components/breadcrumb/breadcrumb.interface';
import { CardComponent } from '@shared/src/lib/components/card/card.component';
import { StatusChipComponent } from '@shared/src/lib/components/status-chip/status-chip.component';
import { StepperItem } from '@shared/src/lib/components/stepper/stepper-item/stepper-item.interface';
import { StepperComponent } from '@shared/src/lib/components/stepper/stepper.component';
import { InformationIconComponent } from '@shared/src/lib/icons/information-icon/information-icon.component';
import { CommonModule } from '@angular/common';
import { StatusChip } from '@shared/src/lib/components/status-chip/status-chip.interface';
import { AdirService } from '@projects/adir/src/app/services/adir.service';
import { WorkflowHistory } from '@projects/adir/src/app/models/adir.interface';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MilestoneDetailsComponent } from '@shared/src/lib/components/milestone-details/milestone-details.component';
import { CalendarEmptyIconComponent } from '@shared/src/lib/icons/calendar-empty-icon/calendar-empty-icon.component';
import { MilestoneAndProjectInvolvementService } from '@projects/adir/src/app/pages/adir-details/milestone-review-request-form/milestone-and-project-involvement-form/milestone-and-project-involvement-form.service';
import { MilestoneAndProjectInvolvement } from '@projects/adir/src/app/pages/adir-details/milestone-review-request-form/milestone-and-project-involvement-form/milestone-and-project-involvement.interface';
import { ADIRSummary } from '../adir-details.interface';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { AdirDetailsSideNavigation } from '@projects/adir/src/app/pages/adir-details/adir-details-side-navigation/adir-details-side-navigation.interface';

@Component({
  selector: 'app-adir-details-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    CardComponent,
    BreadcrumbComponent,
    StatusChipComponent,
    InformationIconComponent,
    StepperComponent,
    AdirDetailsSideNavigationComponent,
    ToastModule,
    MilestoneDetailsComponent,
    CalendarEmptyIconComponent,
  ],
  templateUrl: './adir-details-layout.component.html',
  providers: [MessageService],
})
export class AdirDetailsLayoutComponent implements OnInit, OnDestroy {
  ID = 0;
  milestoneReviewId = '';
  readonly STATUS = StatusChip.AdirStatus;
  workflowHistory: WorkflowHistory.Request.ResponseData[] = [];
  private saveSubscription: Subscription;

  items: BreadcrumbItem[] = [
    { label: 'ADIR Summary', url: '/' },
    { label: '', url: '/' },
  ];

  sideMenuItems: ActionTile.Option[];

  workflowSteps: StepperItem.WorkflowHistory[] = [
    {
      featureId: 1,
      workflows: [
        {
          workflowName: StepperItem.STEPPER_OPTIONS_LABEL.MILESTONE_REVIEW_SUBMISSION,
          isCompleted: false,
          isHidden: false,
          isPreviousCompleted: false,
          isLastElement: false,
        },
        {
          workflowName: StepperItem.STEPPER_OPTIONS_LABEL.PENDING_REVIEW_TRC_AM,
          isCompleted: false,
          isHidden: false,
          isPreviousCompleted: false,
          isLastElement: true,
        },
      ],
    },
  ];

  responseData: MilestoneAndProjectInvolvement.List.FormData;
  projectList: ADIRSummary.ProjectMileStoneReview[] = [];
  trcSlot: any = '-';
  projectsInvolved = '-';
  milestone = '-';
  currentStep: StepperItem.WorkflowOption | null = null;
  requestWorkflowStatus: StepperItem.WorkflowStatus;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly adirService: AdirService,
    private readonly milestoneAndProjectInvolvementService: MilestoneAndProjectInvolvementService
  ) {}

  get getTrcSlotDetails(): string {
    if (this.trcSlot === '-' || !this.trcSlot) {
      return '-';
    }
    const formattedStartTime = dayjs(this.trcSlot.startTime).format('ddd DD/MM/YYYY h:mma');
    const formattedEnd = dayjs(this.trcSlot.endTime).format('h:mma');
    return `${formattedStartTime} - ${formattedEnd}`;
  }

  get milestoneRequestStatus(): string {
    return this.requestWorkflowStatus?.statusName ?? this.STATUS.DRAFT;
  }

  get chipStatusType(): string {
    return StatusChip.StatusType.ADIR;
  }

  get isMomDisabled(): boolean {
    return this.requestWorkflowStatus?.featureId == 1;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.ID = params['ID'] ? Number(params['ID']) : 0;
      const encodedReviewId = params['reviewId'];
      this.milestoneReviewId = encodedReviewId ? decodeURIComponent(encodedReviewId) : '';
      this.items[1].label = this.milestoneReviewId;

      this.sideMenuItems = [
        {
          label: AdirDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.MILESTONE_REVIEW_REQUEST_FORM,
          link: `/adir/${this.ID}/milestone-reviews`,
          iconName: 'file-text',
          isDisabled: false,
        },
        {
          label: AdirDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.MINUTES_OF_MEETING,
          link: `/adir/${this.ID}/minutes-of-meeting`,
          iconName: 'clipboard',
          isDisabled: true,
        },
        {
          label: AdirDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.ATTACHMENTS,
          link: `/adir/${this.ID}/attachments`,
          iconName: 'attachment',
          isDisabled: false,
        },
        {
          label: AdirDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.ACTIVITY_LOGS,
          link: `/adir/${this.ID}/activity-logs`,
          iconName: 'time-stopwatch',
          isDisabled: false,
        },
      ];
    });

    const [defaultStep] = this.workflowSteps[0].workflows;
    this.currentStep = defaultStep;

    this.getWorkflowHistory();

    this.milestoneAndProjectInvolvementService.getListProjectInvolves(this.ID).subscribe({
      next: (resp) => {
        this.projectList = resp;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error getting projects involved',
          detail: '',
          life: 2000,
        });
      },
      complete: () => {
        if (this.ID) {
          this.populateFormData();
        }
      },
    });

    this.adirService.getMilestoneRequestStatus({ requestId: this.ID }).subscribe({
      next: (resp) => {
        this.requestWorkflowStatus = resp;
        this.adirService.sendFlowWorkData(resp);
      },
    });

    this.saveSubscription = this.milestoneAndProjectInvolvementService.milestoneSaveEvent.subscribe(() => {
      this.populateFormData();
    });
  }

  selectedTile(selectedActionTile: ActionTile.Option) {
    this.router.navigate([selectedActionTile.link]);
  }

  populateFormData() {
    this.milestoneAndProjectInvolvementService.getMilestoneFormByRequestId({ requestId: this.ID }).subscribe({
      next: (resp) => {
        const [firstData] = resp;
        this.responseData = firstData;
      },
      complete: () => {
        if (this.responseData) {
          const projectInvolved = this.projectList.find((project) => {
            return project.projectID === this.responseData.projectId;
          });

          if (projectInvolved) {
            this.projectsInvolved = projectInvolved.projectName;
          }

          const { milestones } = this.responseData;
          if (milestones.length > 0) {
            this.milestone = milestones.map((milestone) => milestone.milestoneName).join(', ');
          }

          if (this.responseData.epochStartTime && this.responseData.epochEndTime) {
            this.trcSlot = {
              startTime: new Date(this.responseData.epochStartTime),
              endTime: new Date(this.responseData.epochEndTime),
            };
          }
        }
      },
    });
  }

  getWorkflowHistory() {
    this.adirService.getWorkflowHistoryByRequestId({ requestId: this.ID }).subscribe({
      next: (resp) => {
        this.workflowHistory = resp;
      },
      complete: () => {
        this.updateWorkflowProgress(this.workflowHistory);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error fetching workflow history',
          detail: '',
          life: 2000,
        });
      },
    });
  }

  updateWorkflowProgress(steps: StepperItem.WorkflowHistory[]) {
    if (!this.currentStep) {
      const [firstFeature] = steps;
      const [firstWorkflow] = firstFeature.workflows;
      this.currentStep = firstWorkflow;
    }

    this.workflowSteps = this.enrichStepMetadata(steps);
    this.currentStep = this.determineCurrentStep(this.workflowSteps);
    this.adirService.setCurrentStep(this.currentStep);
  }

  private enrichStepMetadata(steps: StepperItem.WorkflowHistory[]): StepperItem.WorkflowHistory[] {
    return steps.map((featureHistory) => ({
      ...featureHistory,
      workflows: featureHistory.workflows.map((workflow, index) => ({
        ...workflow,
        isPreviousCompleted: index > 0 ? featureHistory.workflows[index - 1].isCompleted : false,
        isLastElement: this.determineIsLastElement(
          index,
          workflow.isHidden,
          featureHistory.featureId,
          featureHistory.workflows.length
        ),
      })),
    }));
  }

  determineIsLastElement(index: number, isWorkflowHidden: boolean, featureId: number, workflowLength: number): boolean {
    if (featureId === 1) {
      return !isWorkflowHidden && index >= workflowLength - 2;
    }
    return index == workflowLength - 1;
  }

  private isAllFeatureWorkflowsCompleted(steps: StepperItem.WorkflowHistory[]): boolean {
    return steps.every((feature) => feature.workflows.every((workflow) => workflow.isCompleted));
  }

  private findNextIncompleteStep(steps: StepperItem.WorkflowHistory[]): StepperItem.WorkflowHistory | null {
    return (
      steps.find((step) => {
        return step.workflows.find((workflow) => {
          return workflow.isCompleted === false;
        });
      }) ?? null
    );
  }

  private determineCurrentStep(steps: StepperItem.WorkflowHistory[]): StepperItem.WorkflowOption | null {
    const nextIncompleteStep = this.findNextIncompleteStep(steps);
    if (nextIncompleteStep) {
      return nextIncompleteStep.workflows.find((workflow) => !workflow.isCompleted)!;
    }

    const isAllFeatureWorkflowsCompleted = this.isAllFeatureWorkflowsCompleted(steps);

    if (isAllFeatureWorkflowsCompleted) {
      return steps[steps.length - 1].workflows.find((worklow) => worklow.isLastElement) ?? null;
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }
}
